import React, { useEffect, useState } from 'react';
import { usePlaidLink, PlaidLinkOnSuccess, PlaidLinkOnExit, PlaidLinkError, PlaidLinkOnEvent } from 'react-plaid-link';
import { showSuccessNotification, showErrorNotification } from '../utils/notificationUtils';
import { env } from '../utils/env';

interface LinkUpdateHandlerProps {
    linkToken: string;
    accessToken: string;
    itemId?: string;
    onSuccess: () => void;
    onExitCallback?: (error: PlaidLinkError | null) => void;
    isOauth?: boolean;
    accountSelectionEnabled?: boolean;
}

/**
 * Component that handles both Plaid Link new account and update mode flows
 * Uses the usePlaidLink hook to manage Plaid Link creation
 */
const LinkUpdateHandler: React.FC<LinkUpdateHandlerProps> = ({
    linkToken,
    accessToken,
    itemId,
    onSuccess,
    onExitCallback,
    isOauth = false,
    accountSelectionEnabled = false
}) => {
    // Treat any truthy itemId as update mode; undefined / null / empty string => new account mode
    const isUpdateMode = itemId !== undefined && itemId !== null && itemId !== '';
    const [currentLinkToken, setCurrentLinkToken] = useState(linkToken);

    // Method to refresh the link token if it expires
    const refreshLinkToken = async () => {
        try {
            console.log("Link token expired, refreshing token...");

            if (isUpdateMode && itemId) {
                // For update mode, refresh the token for the specific item
                const redirect_uri = `${window.location.origin}/plaid-oauth-redirect`;

                const response = await fetch(`${env.API_URL}/api/financial/items/${itemId}/refresh`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json'
                    },
                    credentials: 'include',
                    body: JSON.stringify({
                        redirect_uri,
                        force_reauthorization: true,
                        account_selection_enabled: accountSelectionEnabled,
                    }),
                });

                if (!response.ok) {
                    throw new Error('Failed to refresh link token');
                }

                const data = await response.json();
                setCurrentLinkToken(data.link_token);
            } else {
                // For new accounts, get a new link token
                const redirect_uri = `${window.location.origin}/plaid-oauth-redirect`;

                const response = await fetch(`${env.API_URL}/api/financial/link/token`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json'
                    },
                    credentials: 'include',
                    body: JSON.stringify({
                        redirect_uri,
                    }),
                });

                if (!response.ok) {
                    throw new Error('Failed to create new link token');
                }

                const data = await response.json();
                setCurrentLinkToken(data.link_token);
            }
        } catch (err) {
            console.error('Error refreshing link token:', err);
            showErrorNotification(err, 'Failed to refresh session. Please try again.');
        }
    };

    // Define onSuccess handler for Link
    const handleSuccess: PlaidLinkOnSuccess = async (publicToken, metadata) => {
        try {
            console.log(`Link ${isUpdateMode ? 'update mode' : 'new account'} completed successfully`, metadata);
            console.log(`Public token received: ${publicToken.substring(0, 5)}...${publicToken.substring(publicToken.length - 5)}`);

            if (isUpdateMode) {
                // For update mode, we don't need to exchange the public token
                showSuccessNotification('Account reconnected successfully');
                onSuccess();
            } else {
                // For new accounts, we need to exchange the public token
                console.log('Exchanging public token with backend...');
                console.log(`API URL: ${env.API_URL}/api/financial/link/exchange`);
                console.log(`Authorization header: Bearer ${accessToken.substring(0, 5)}...`);

                // Store the public token and metadata in localStorage BEFORE any exchange attempt
                // This ensures they're available for the fallback path in AccountsTab
                localStorage.setItem('plaid_public_token', publicToken);
                localStorage.setItem('plaid_metadata', JSON.stringify(metadata));
                localStorage.setItem('plaid_exchange_completed', 'false');

                try {
                    const response = await fetch(`${env.API_URL}/api/financial/link/exchange`, {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${accessToken}`,
                            'Content-Type': 'application/json',
                        },
                        credentials: 'include',
                        body: JSON.stringify({
                            public_token: publicToken,
                            metadata
                        }),
                    });

                    console.log('Exchange response status:', response.status);

                    // Log full request details for debugging
                    console.log('Request details:', {
                        url: `${env.API_URL}/api/financial/link/exchange`,
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${accessToken.substring(0, 5)}...`,
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            public_token: `${publicToken.substring(0, 5)}...`,
                            metadata
                        }),
                    });

                    if (!response.ok) {
                        const errorText = await response.text();
                        console.error('Raw error response:', errorText);

                        let errorData: {
                            error_code?: string;
                            error_message?: string;
                            detail?: string;
                        } = {};
                        try {
                            errorData = JSON.parse(errorText);
                        } catch (e) {
                            console.error('Failed to parse error response as JSON:', e);
                        }

                        console.error('Exchange error response:', errorData);

                        // Check for duplicate institution error
                        if (errorData.error_code === 'ITEM_ERROR' &&
                            errorData.error_message &&
                            errorData.error_message.includes('already linked')) {
                            throw new Error(`You have already linked this institution to your account.`);
                        }

                        throw new Error(errorData.detail || 'Failed to link account');
                    }

                    const exchangeData = await response.json();
                    console.log('Exchange successful, received data:', exchangeData);
                    console.log('Exchange successful, received item_id:', exchangeData.item_id);
                    console.log('Exchange successful, received accounts:', exchangeData.accounts?.length || 0);

                    // Mark exchange as completed to prevent duplicate exchanges
                    localStorage.setItem('plaid_exchange_completed', 'true');

                    // Success case - clean up localStorage to prevent future duplicate exchanges
                    localStorage.removeItem('plaid_public_token');
                    localStorage.removeItem('plaid_metadata');

                    showSuccessNotification('Account linked successfully');
                    onSuccess();
                } catch (fetchError) {
                    console.error('Network error during token exchange:', fetchError);
                    // Call onExitCallback when exchange fails
                    onExitCallback?.(fetchError as PlaidLinkError);
                    throw fetchError; // Re-throw to be caught by outer catch if needed
                }
            }
        } catch (err) {
            console.error(`Error in ${isUpdateMode ? 'update mode' : 'new account'} success handler:`, err);
            showErrorNotification(err, `Failed to ${isUpdateMode ? 'reconnect' : 'link'} account`);
            // Optionally call onExitCallback here too, although the inner catch should handle it
            // onExitCallback?.(err as PlaidLinkError);
        }
    };

    // Define onExit handler for Link
    const handleExit: PlaidLinkOnExit = (error, metadata) => {
        console.log(`Link ${isUpdateMode ? 'update mode' : 'new account'} exited`, error, metadata);

        if (error) {
            // Special handling for INVALID_LINK_TOKEN
            if (error.error_code === 'INVALID_LINK_TOKEN') {
                refreshLinkToken();
                return;
            }

            // Detailed error handling for other codes
            const errorMessage =
                error.error_code === 'INSTITUTION_ERROR' ? 'Unable to connect to institution at this time' :
                    error.error_code === 'RATE_LIMIT_EXCEEDED' ? 'Too many requests, please try again later' :
                        error.error_code === 'ITEM_LOGIN_REQUIRED' ? 'Please re-authenticate with your institution' :
                            error.error_code === 'INSTITUTION_NOT_RESPONDING' ? 'Institution is not responding, please try again later' :
                                error.error_code === 'ADDITIONAL_CONSENT_REQUIRED' ? 'Additional consent is required for this connection. Please reconnect your account.' :
                                    error.display_message ||
                                    error.error_message ||
                                    `Error during account ${isUpdateMode ? 'reconnection' : 'linking'}`;

            showErrorNotification(error, errorMessage);
        }
        // Call the callback regardless of error status
        onExitCallback?.(error);
    };

    // Define onEvent handler for Link
    const handleEvent: PlaidLinkOnEvent = (eventName, metadata) => {
        console.log(`Link ${isUpdateMode ? 'update mode' : 'new account'} event`, eventName, metadata);

        // Handle errors in event that user doesn't exit with error enabled
        if (eventName === 'ERROR' && metadata.error_code) {
            showErrorNotification({ error_code: metadata.error_code }, 'An error occurred');
        }
    };

    // Configure the Link instance
    const config: any = {
        token: currentLinkToken, // Use the current token which may have been refreshed
        onSuccess: handleSuccess,
        onExit: handleExit,
        onEvent: handleEvent,
    };

    // Add receivedRedirectUri for OAuth flow
    if (isOauth) {
        config.receivedRedirectUri = window.location.href;
    }

    // Use the Plaid Link hook
    const { open, ready } = usePlaidLink(config);

    // Open Link automatically when ready
    useEffect(() => {
        if (ready) {
            // Store configuration for OAuth redirect if needed
            localStorage.setItem(
                'plaidUpdateConfig',
                JSON.stringify({
                    isUpdateMode,
                    itemId,
                    token: currentLinkToken,
                })
            );

            // Also store the token in the format used by PlaidOAuthRedirect component
            localStorage.setItem('plaid_link_token', currentLinkToken);

            // Open the Link interface
            open();
        }
    }, [ready, open, itemId, currentLinkToken, isUpdateMode]);

    // This component doesn't render anything visible
    return null;
};

export default LinkUpdateHandler; 